import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { db } from '@root/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

/**
 * VendorKpiCards
 * Renders 7 KPI cards:
 *  1) Active adverts
 *  2) Future adverts
 *  3) Number of products
 *  4) Best product rating (ratingAvg)
 *  5) Worst product rating (ratingAvg)
 *  6) Best advert by totalViews
 *  7) Best product by totalViews
 *
 * Props:
 * - uid (string): required – the vendor/user id
 */
export default function VendorKpiCards({ uid }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]); // all marketplace items for this uid

  useEffect(() => {
    if (!uid) return;
    (async () => {
      setLoading(true);
      try {
        const qRef = query(
          collection(db, 'marketplace'),
          where('uid', '==', uid)
        );
        const snap = await getDocs(qRef);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(rows);
      } catch (e) {
        console.error('[VendorKpiCards] load error', e);
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  // -------- helpers --------
  const tsToDayjs = (ts) => {
    if (!ts) return null;
    if (ts.toDate) return dayjs(ts.toDate());
    return dayjs(ts);
  };

  const isActiveAdvert = (ad) => {
    // Prefer stored status if present
    if (ad?.status) return ad.status === 'active';
    // Fallback to dates
    const now = dayjs();
    const start = tsToDayjs(ad?.startDate);
    const end = tsToDayjs(ad?.endDate);
    if (!start) return false;
    if (end) return now.isAfter(start) && now.isBefore(end.add(1, 'day'));
    return now.isAfter(start) || now.isSame(start, 'day');
  };

  const isFutureAdvert = (ad) => {
    if (ad?.status) return ad.status === 'future';
    const now = dayjs();
    const start = tsToDayjs(ad?.startDate);
    if (!start) return false;
    return start.isAfter(now, 'day');
  };

  // -------- derived metrics --------
  const {
    activeAdverts,
    futureAdverts,
    products,
    bestRatedProduct,
    worstRatedProduct,
    bestViewsAdvert,
    bestViewsProduct,
  } = useMemo(() => {
    const adverts = items.filter((x) => x.type === 'adverts');
    const products = items.filter((x) => x.type === 'products');

    const activeAdverts = adverts.filter(isActiveAdvert);
    const futureAdverts = adverts.filter(isFutureAdvert);

    const numeric = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

    // Best / worst product by ratingAvg (ignore unrated = 0)
    const ratedProducts = products
      .map((p) => ({ ...p, ratingAvg: numeric(p.ratingAvg) }))
      .filter((p) => p.ratingAvg > 0);

    const bestRatedProduct =
      ratedProducts.length > 0
        ? ratedProducts.reduce((a, b) => (a.ratingAvg >= b.ratingAvg ? a : b))
        : null;

    const worstRatedProduct =
      ratedProducts.length > 0
        ? ratedProducts.reduce((a, b) => (a.ratingAvg <= b.ratingAvg ? a : b))
        : null;

    // Best views by type
    const bestViewsAdvert =
      adverts.length > 0
        ? adverts.reduce((a, b) =>
            numeric(a.totalViews) >= numeric(b.totalViews) ? a : b
          )
        : null;

    const bestViewsProduct =
      products.length > 0
        ? products.reduce((a, b) =>
            numeric(a.totalViews) >= numeric(b.totalViews) ? a : b
          )
        : null;

    return {
      activeAdverts,
      futureAdverts,
      products,
      bestRatedProduct,
      worstRatedProduct,
      bestViewsAdvert,
      bestViewsProduct,
    };
  }, [items]);

  // Small presentational helpers
  const Card = ({ title, value, sub, color = 'primary', additional }) => (
    <div className='col-xl-2 col-sm-6'>
      <div className='card'>
        <div
          className='card-body d-flex px-4  justify-content-between'
          style={{ position: 'relative' }}
        >
          <div>
            <div className=''>
              <h2 className='fs-32 font-w700'>
                {loading ? '…' : value}
                {additional ? (
                  <small className='text-primary fs-12'> / 5</small>
                ) : (
                  ''
                )}
              </h2>
              <span className={`fs-12 font-w500 d-block`}>{title}</span>
              <span className={`fs-12 font-w500 d-block text-${color}`}>
                {sub}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const fmtRating = (n) =>
    typeof n === 'number' && !Number.isNaN(n)
      ? Number.isInteger(n)
        ? n
        : n.toFixed(1)
      : '—';

  const trim = (s, max = 28) =>
    s ? (s.length > max ? s.slice(0, max - 1) + '…' : s) : '—';

  return (
    <div className='row'>
      <Card
        title='Active Adverts'
        value={activeAdverts?.length ?? 0}
        color='success'
      />
      <Card
        title='Future Adverts'
        value={futureAdverts?.length ?? 0}
        color='warning'
      />
      <Card
        title='Total Products'
        value={products?.length ?? 0}
        color='primary'
      />
      <Card
        title='Highest Rating'
        value={fmtRating(bestRatedProduct?.ratingAvg)}
        additional={true}
        sub={trim(bestRatedProduct?.product || bestRatedProduct?.title)}
        color='success'
      />
      <Card
        title='Lowest Rating'
        value={fmtRating(worstRatedProduct?.ratingAvg)}
        additional={true}
        sub={trim(worstRatedProduct?.product || worstRatedProduct?.title)}
        color='danger'
      />
      <Card
        title='Top Advert Views'
        value={bestViewsAdvert?.totalViews ?? 0}
        sub={trim(bestViewsAdvert?.title)}
        color='info'
      />
      <Card
        title='Top Product Views'
        value={bestViewsProduct?.totalViews ?? 0}
        sub={trim(bestViewsProduct?.product || bestViewsProduct?.title)}
        color='info'
      />
    </div>
  );
}
